import { useMsal } from '@azure/msal-react';
import type { AppRole } from '../constants/appRoles';

export interface UseAppRolesResult {
  roles: string[];
  hasRole: (role: AppRole) => boolean;
  hasAnyRole: (roles: AppRole[]) => boolean;
}

/** localStorage key holding a JSON array of roles to impersonate in dev builds. */
export const DEV_ROLES_STORAGE_KEY = 'devRoles';

/**
 * Development-only role impersonation, used to exercise the entitlement and
 * navigation experiences without reassigning app roles in Entra ID. Ignored in
 * production builds, and it only changes what the UI shows — the API remains
 * the enforcement point.
 *
 * Usage in the browser console while running `npm run dev`:
 *   localStorage.setItem('devRoles', '["PDM.ReadOnly"]'); location.reload();
 *   localStorage.removeItem('devRoles');  // back to the real token roles
 */
function readDevRolesOverride(): string[] | undefined {
  if (!import.meta.env.DEV) return undefined;

  try {
    const raw = window.localStorage.getItem(DEV_ROLES_STORAGE_KEY);
    if (raw === null) return undefined;

    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.some((role) => typeof role !== 'string')) {
      console.warn(`[useAppRoles] "${DEV_ROLES_STORAGE_KEY}" must be a JSON array of strings.`);
      return undefined;
    }

    return parsed as string[];
  } catch {
    console.warn(`[useAppRoles] Could not parse "${DEV_ROLES_STORAGE_KEY}" from localStorage.`);
    return undefined;
  }
}

/**
 * Reads the app roles assigned to the current user from MSAL's ID token claims.
 * The `roles` claim is populated by Azure AD when the user has been assigned
 * app roles in the enterprise application. Each value matches the `Value` field
 * of the app role definition — see `constants/appRoles.ts`.
 *
 * In development builds a `devRoles` localStorage entry overrides the claim.
 */
export function useAppRoles(): UseAppRolesResult {
  const { accounts } = useMsal();
  const account = accounts[0];

  const claimRoles: string[] = (account?.idTokenClaims?.roles as string[] | undefined) ?? [];
  const roles: string[] = readDevRolesOverride() ?? claimRoles;

  const hasRole = (role: AppRole) => roles.includes(role);
  const hasAnyRole = (rolesToCheck: AppRole[]) => rolesToCheck.some((r) => roles.includes(r));

  return { roles, hasRole, hasAnyRole };
}
